import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import api from '../../services/baseAPIService';

const PaywallComponent = ({ onClose, canClose = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [withdrawalWaiverAccepted, setWithdrawalWaiverAccepted] = useState(false);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && canClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [canClose, onClose]);

  const features = [
    {
      title: "Spezialisiert auf SKS-Theorieprüfung",
      description: "Fokussierte Vorbereitung auf die schriftlichen Prüfungsfragen der SKS-Theorie"
    },
    {
      title: "Umfassendes SKS-Theoriewissen",
      description: "Alle relevanten Themengebiete der SKS-Theorieprüfung auf einer Website"
    },
    {
      title: "Prüfungsrelevante Lernmethode",
      description: "Spaced-Learning-System optimiert für die SKS-Theorieprüfung"
    },
    {
      title: "Flexibles SKS-Lernen",
      description: "Lerne die SKS-Theorie mobil auf Smartphone und Tablet"
    }
  ];

  const handleStripeCheckout = async () => {
    if (!agbAccepted || !withdrawalWaiverAccepted) {
      alert('Bitte akzeptiere die AGB und stimme dem Erlöschen des Widerrufsrechts zu, um fortzufahren.');
      return;
    }

    try {
      setIsLoading(true);
      
      const response = await api.post('/payments/create-checkout-session');
      
      if (!response.data?.checkout_url) {
        throw new Error('No checkout URL received');
      }
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Stripe checkout failed:', error);
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={(e) => {
        if (canClose && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="card bg-base-100 w-full max-w-2xl shadow-xl relative my-4 sm:my-2">
        <div className="card-body p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="text-center mb-4">
            {canClose ? (
              <>
                <h2 className="text-xl sm:text-2xl font-bold">Upgrade zur Vollversion</h2>
                <p className="text-base-content/70 mt-2 text-sm sm:text-base">
                  Du kannst die App gerne weiterhin mit deinem Testvolumen nutzen. Wenn du jetzt schon von unserer App überzeugt bist, kannst du hier zur Vollversion upgraden und unbegrenzten Zugriff erhalten.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-xl sm:text-2xl font-bold">Testvolumen erreicht</h2>
                <p className="text-base-content/70 mt-2 text-sm sm:text-base">
                  Du hast das kostenlose Testvolumen erreicht. Sichere dir jetzt den unbegrenzten Zugriff auf alle Funktionen.
                </p>
              </>
            )}
          </div>

          {/* Features */}
          <div className="space-y-3 mb-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="mt-1 shrink-0">
                  <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-success" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm sm:text-base">{feature.title}</h3>
                  <p className="text-base-content/70 text-xs sm:text-sm">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Price */}
          <div className="bg-base-200 rounded-lg p-4 mb-4">
            <div className="text-center">
              <span className="text-2xl sm:text-3xl font-bold">19,99 €</span>
              <span className="text-base-content/70 ml-2 text-sm sm:text-base block sm:inline">/ für 1 Jahr Zugriff ohne automatische Verlängerung</span>
            </div>
          </div>

          {/* Terms Acceptance */}
          <div className="space-y-3 mb-4">
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                className="checkbox checkbox-primary mt-1 checkbox-sm"
                checked={agbAccepted}
                onChange={(e) => setAgbAccepted(e.target.checked)}
              />
              <label className="text-xs sm:text-sm">
                Ich habe die <Link to="/agb" target="_blank" className="link link-primary">Allgemeinen Geschäftsbedingungen (AGB)</Link> gelesen und akzeptiere diese.
              </label>
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                className="checkbox checkbox-primary mt-1 checkbox-sm"
                checked={withdrawalWaiverAccepted}
                onChange={(e) => setWithdrawalWaiverAccepted(e.target.checked)}
              />
              <label className="text-xs sm:text-sm">
                Ich stimme ausdrücklich zu, dass die digitalen Lerninhalte sofort nach Vertragsschluss zur Verfügung gestellt werden. Mir ist bekannt, dass mein Widerrufsrecht mit Beginn der Ausführung des Vertrags erlischt (§ 356 Abs. 5 BGB). Details hierzu finde ich in den <Link to="/agb" target="_blank" className="link link-primary">AGB</Link>.
              </label>
            </div>
          </div>

          {/* Payment Button */}
          <button
            onClick={handleStripeCheckout}
            className="btn btn-primary w-full text-white text-sm sm:text-base"
            disabled={isLoading || !agbAccepted || !withdrawalWaiverAccepted}
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                <span>Wird verarbeitet...</span>
              </>
            ) : 'Jetzt kaufen - Weiterleitung zum Zahlungsabwickler'}
          </button>

          {/* Exit Link/Button */}
          <div className="text-center mt-3">
            {canClose ? (
              <button 
                onClick={onClose}
                className="link link-primary text-xs sm:text-sm"
                disabled={isLoading}
              >
                Fenster schließen (ESC)
              </button>
            ) : (
              <Link 
                to="/home" 
                className="link link-primary text-xs sm:text-sm"
                tabIndex={isLoading ? -1 : 0}
                aria-disabled={isLoading}
              >
                Zurück zur Übersicht
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaywallComponent; 